import { cajaContenido, cajaDecorativo, MARGEN_SECTOR } from "./geometria";
import { juntarPartes, separarPartes, sincronizarPartes } from "./partes";
import { desdeGuardado, paraGuardado } from "./tarifasNuevas";

export const MODO = { POR_ZONA: "por_zona", MAPA_EVENTO: "mapa_evento" };

export const COLORES = ["#5656ff", "#00a884", "#ff8a00", "#e5484d", "#8e4ec6", "#0091ff", "#d4a017", "#30a46c"];

let contador = 0;
export const nuevaClave = (prefijo) => `${prefijo}-${Date.now().toString(36)}-${(contador++).toString(36)}`;

export const conLugares = (sector) => sector.filas.length > 0 || sector.mesas.length > 0;

export const cantidadAsientos = (sector) =>
  sector.filas.reduce((total, f) => total + f.asientos.length, 0) +
  sector.mesas.reduce((total, m) => total + m.sillas.length, 0);

export const capacidadSector = (sector) => (conLugares(sector) ? cantidadAsientos(sector) : Number(sector.capacidad) || 0);

export const capacidadTotal = (mapa) => mapa.sectores.reduce((total, s) => total + capacidadSector(s), 0);

const conVentas = (sector) =>
  sector.con_ventas ||
  sector.filas.some((f) => f.asientos.some((a) => a.vendido)) ||
  sector.mesas.some((m) => m.vendida || m.sillas.some((a) => a.vendido));

export const sePuedeBorrar = (sector) => !conVentas(sector);

export const nombreUnico = (mapa, base, excepto = null) => {
  const usados = new Set(
    mapa.sectores.filter((s) => s.clave !== excepto).map((s) => s.nombre.trim().toLowerCase())
  );
  if (!usados.has(base.trim().toLowerCase())) return base;
  for (let n = 2; ; n++) {
    const candidato = `${base} ${n}`;
    if (!usados.has(candidato.toLowerCase())) return candidato;
  }
};

const conClave = (prefijo) => (item) => (item.clave ? item : { ...item, clave: nuevaClave(prefijo) });

// En los mapas del editor viejo las filas de una zona tienen coordenadas de su
// propio lienzo y no entran en la caja chica de la zona: se corre el contenido
// al origen y la caja crece hasta cubrirlo.
const acomodarContenido = (sector) => {
  const caja = cajaContenido(sector);
  if (!caja) return sector;
  const entra =
    caja.minX >= 0 && caja.minY >= 0 && caja.maxX <= sector.ancho && caja.maxY <= sector.alto;
  if (entra) return sector;

  const dx = Math.round(MARGEN_SECTOR - caja.minX);
  const dy = Math.round(MARGEN_SECTOR - caja.minY);
  return {
    ...sector,
    ancho: Math.ceil(caja.maxX - caja.minX + 2 * MARGEN_SECTOR),
    alto: Math.ceil(caja.maxY - caja.minY + 2 * MARGEN_SECTOR),
    filas: sector.filas.map((f) => ({
      ...f,
      x: f.x + dx,
      y: f.y + dy,
      asientos: f.asientos.map((a) => ({ ...a, x: a.x + dx, y: a.y + dy })),
    })),
    mesas: sector.mesas.map((m) => ({ ...m, x: m.x + dx, y: m.y + dy })),
    decorativos: sector.decorativos.map((d) => ({
      ...d,
      x: d.x + dx,
      y: d.y + dy,
      ...(d.tipo === "linea"
        ? { punto_final_x: (d.punto_final_x ?? d.x) + dx, punto_final_y: (d.punto_final_y ?? d.y) + dy }
        : {}),
    })),
  };
};

const SEPARACION = 40;

const seSuperponen = (a, b) =>
  a.x < b.x + b.ancho && b.x < a.x + a.ancho && a.y < b.y + b.alto && b.y < a.y + a.alto;

// En los mapas del editor viejo las zonas eran cajas chicas: al crecer hasta
// cubrir sus asientos se pisan. Si pasa, se reacomodan en filas en el orden en
// que estaban, y el plano crece hasta que entre todo.
const acomodarSectores = (mapa) => {
  const visibles = mapa.sectores.filter((s) => s.en_mapa);
  const encimados = visibles.some((a, i) => visibles.slice(i + 1).some((b) => seSuperponen(a, b)));

  let sectores = mapa.sectores;
  if (encimados) {
    const anchoFila = Math.max(mapa.ancho, ...visibles.map((s) => s.ancho + 2 * SEPARACION));
    const orden = [...visibles].sort((a, b) => a.y - b.y || a.x - b.x);
    const posiciones = {};
    let x = SEPARACION;
    let y = SEPARACION;
    let altoFila = 0;
    orden.forEach((s) => {
      if (x + s.ancho > anchoFila - SEPARACION && x > SEPARACION) {
        x = SEPARACION;
        y += altoFila + SEPARACION;
        altoFila = 0;
      }
      posiciones[s.clave] = { x, y, rotacion: 0 };
      x += s.ancho + SEPARACION;
      altoFila = Math.max(altoFila, s.alto);
    });
    sectores = mapa.sectores.map((s) => (posiciones[s.clave] ? { ...s, ...posiciones[s.clave] } : s));
  }

  const enMapa = sectores.filter((s) => s.en_mapa);
  const ancho = Math.max(mapa.ancho, ...enMapa.map((s) => s.x + s.ancho + SEPARACION));
  const alto = Math.max(mapa.alto, ...enMapa.map((s) => s.y + s.alto + SEPARACION));
  return { ...mapa, sectores, ancho: Math.ceil(ancho), alto: Math.ceil(alto) };
};

// normalizarMapa deja el documento listo para editar: listas siempre
// presentes, claves para todo lo seleccionable y el contenido viejo acomodado.
// esViejo: el mapa nunca se publicó con este editor (viene del de zonas) y
// sus sectores se pueden reacomodar si quedan encimados.
export const normalizarMapa = (mapa, { esViejo = false } = {}) => {
  const normalizado = {
  modo: mapa.modo || MODO.POR_ZONA,
  ancho: mapa.ancho || 1600,
  alto: mapa.alto || 1000,
  fondo: mapa.fondo || "",
  decorativos: (mapa.decorativos || []).map(conClave("d")),
  tarifas_nuevas: mapa.tarifas_nuevas || [],
  sectores: (mapa.sectores || []).map(desdeGuardado).map((s) => ({
      ...s,
      clave: s.clave || nuevaClave("s"),
      ancho: s.ancho || 200,
      alto: s.alto || 120,
      rotacion: s.rotacion || 0,
      forma: s.forma || [],
      filas: (s.filas || []).map((f) => ({ ...f, asientos: f.asientos || [] })),
      mesas: (s.mesas || []).map((m) => conClave("m")({ ...m, sillas: m.sillas || [] })),
      decorativos: (s.decorativos || []).map(conClave("d")),
      tarifas: s.tarifas || [],
  })),
  };
  // las partes se separan antes de acomodar: juntas, sus lugares quedan fuera de la caja
  const separado = separarPartes(normalizado);
  const acomodado = { ...separado, sectores: separado.sectores.map(acomodarContenido) };
  return esViejo ? acomodarSectores(acomodado) : acomodado;
};

export const crearSectorGeneral = (mapa, { x, y, enMapa = true }) => {
  const color = COLORES[mapa.sectores.length % COLORES.length];
  return {
    clave: nuevaClave("s"),
    nombre: nombreUnico(mapa, "Ubicación"),
    en_mapa: enMapa,
    x,
    y,
    ancho: 200,
    alto: 120,
    rotacion: 0,
    color,
    forma: [],
    limite_persona: 10,
    capacidad: 100,
    filas: [],
    mesas: [],
    decorativos: [],
    tarifas: [],
  };
};

export const agregarSector = (mapa, sector) => ({ ...mapa, sectores: [...mapa.sectores, sector] });

export const actualizarSector = (mapa, clave, cambios) => ({
  ...mapa,
  sectores: mapa.sectores.map((s) => (s.clave === clave ? { ...s, ...cambios } : s)),
});

// quitarSector: al quitar una ubicación se van también sus partes
export const quitarSector = (mapa, clave) => ({
  ...mapa,
  sectores: mapa.sectores.filter((s) => s.clave !== clave && s.editor?.parteDe !== clave),
});

const sinId = ({ id, vendido, vendida, ...resto }) => resto;

// duplicarSector copia el sector como uno nuevo: sin ids ni ventas, sin
// tarifas (una tarifa va en un solo sector) y corrido para que se vea.
export const duplicarSector = (mapa, clave) => {
  const original = mapa.sectores.find((s) => s.clave === clave);
  if (!original) return { mapa, clave: null };
  const { id, con_ventas, ...resto } = original;
  const { parteDe, partes, ...editor } = original.editor || {};
  const copia = {
    ...resto,
    clave: nuevaClave("s"),
    nombre: nombreUnico(mapa, `${original.nombre} (copia)`),
    x: original.x + 30,
    y: original.y + 30,
    tarifas: [],
    editor,
    filas: original.filas.map((f) => ({ ...f, asientos: f.asientos.map(sinId) })),
    mesas: original.mesas.map((m) => ({ ...sinId(m), clave: nuevaClave("m"), sillas: m.sillas.map(sinId) })),
    decorativos: original.decorativos.map((d) => ({ ...d, clave: nuevaClave("d") })),
  };
  return { mapa: agregarSector(mapa, copia), clave: copia.clave };
};

const DECORATIVOS = {
  texto: { texto: "Texto", font_size: 22, color: "#1f2937" },
  rectangulo: { ancho: 160, alto: 80, color: "#cbd5e1" },
  circulo: { ancho: 90, alto: 90, color: "#cbd5e1" },
  linea: { color: "#334155" },
};

export const crearDecorativo = (tipo, { x, y }) => {
  const decorativo = { clave: nuevaClave("d"), tipo, x, y, rotacion: 0, z_index: 0, ...DECORATIVOS[tipo] };
  if (tipo === "linea") {
    decorativo.punto_final_x = x + 160;
    decorativo.punto_final_y = y;
  }
  return decorativo;
};

export const agregarDecorativo = (mapa, decorativo) => ({ ...mapa, decorativos: [...mapa.decorativos, decorativo] });

export const actualizarDecorativo = (mapa, clave, cambios) => ({
  ...mapa,
  decorativos: mapa.decorativos.map((d) => (d.clave === clave ? { ...d, ...cambios } : d)),
});

export const quitarDecorativo = (mapa, clave) => ({
  ...mapa,
  decorativos: mapa.decorativos.filter((d) => d.clave !== clave),
});

// El servidor guarda posiciones y medidas enteras.
const ENTEROS = new Set(["x", "y", "ancho", "alto", "punto_final_x", "punto_final_y", "font_size", "z_index"]);
const redondear = (valor) => {
  if (Array.isArray(valor)) return valor.map(redondear);
  if (!valor || typeof valor !== "object") return valor;
  return Object.fromEntries(
    Object.entries(valor).map(([k, v]) => [
      k,
      k === "editor" ? v : ENTEROS.has(k) && typeof v === "number" ? Math.round(v) : redondear(v),
    ])
  );
};

// paraGuardar saca lo que es sólo del editor antes de mandarlo al servidor.
const MARGEN_PLANO = 60;
const PLANO_VACIO = { x: 0, y: 0, ancho: 1600, alto: 1000 };

// cajaMapa: el rectángulo que ocupa lo dibujado (ubicaciones en el plano,
// giradas, y objetos). El lienzo del editor es infinito: el plano es esto.
export const cajaMapa = (mapa) => {
  const puntos = [];
  mapa.sectores
    .filter((s) => s.en_mapa)
    .forEach((s) => {
      const r = ((s.rotacion || 0) * Math.PI) / 180;
      [[0, 0], [s.ancho, 0], [0, s.alto], [s.ancho, s.alto]].forEach(([dx, dy]) =>
        puntos.push([s.x + dx * Math.cos(r) - dy * Math.sin(r), s.y + dx * Math.sin(r) + dy * Math.cos(r)])
      );
    });
  mapa.decorativos.forEach((d) => {
    const c = cajaDecorativo(d);
    puntos.push([c.minX, c.minY], [c.maxX, c.maxY]);
  });
  if (puntos.length === 0) return PLANO_VACIO;
  const xs = puntos.map((p) => p[0]);
  const ys = puntos.map((p) => p[1]);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, ancho: Math.max(...xs) - x, alto: Math.max(...ys) - y };
};

// normalizarPlano corre todo para que lo dibujado empiece en el margen y el
// plano mida justo eso (la compra recibe un plano con su tamaño, como antes)
const normalizarPlano = (mapa) => {
  const caja = cajaMapa(mapa);
  if (caja === PLANO_VACIO) return { ...mapa, ancho: PLANO_VACIO.ancho, alto: PLANO_VACIO.alto };
  const dx = Math.round(MARGEN_PLANO - caja.x);
  const dy = Math.round(MARGEN_PLANO - caja.y);
  const mover = (d) => ({
    ...d,
    x: d.x + dx,
    y: d.y + dy,
    ...(d.tipo === "linea" ? { punto_final_x: (d.punto_final_x ?? d.x) + dx, punto_final_y: (d.punto_final_y ?? d.y) + dy } : {}),
  });
  return {
    ...mapa,
    ancho: Math.max(100, Math.ceil(caja.ancho + 2 * MARGEN_PLANO)),
    alto: Math.max(100, Math.ceil(caja.alto + 2 * MARGEN_PLANO)),
    sectores: mapa.sectores.map((s) => ({ ...s, x: s.x + dx, y: s.y + dy })),
    decorativos: mapa.decorativos.map(mover),
  };
};

export const paraGuardar = (mapa) => {
  const plano = juntarPartes(normalizarPlano(mapa));
  return redondear({
    ...plano,
    decorativos: plano.decorativos.map(({ clave, ...d }) => d),
    sectores: plano.sectores.map((s) => ({
      ...paraGuardado(s),
      decorativos: s.decorativos.map(({ clave, ...d }) => d),
    })),
  });
};

const COLOR_TARIFA_POR_DEFECTO = "#5656ff";

const aHex = (texto) => {
  try {
    const { r, g, b } = JSON.parse(texto);
    return `#${[r, g, b].map((v) => Math.round((v || 0) * 255).toString(16).padStart(2, "0")).join("")}`;
  } catch {
    return COLOR_TARIFA_POR_DEFECTO;
  }
};

export const COLOR_NEUTRO = "#71717a";

// coloresDeTarifas: el color de cada tarifa en el plano. Si conserva el color
// por defecto, toma uno de la paleta según su id (el mismo en el editor y en
// la compra) para que se distingan entre sí.
export const coloresDeTarifas = (tarifas) => {
  const colores = {};
  tarifas.forEach((t) => {
    const hex = aHex(t.color);
    const enPaleta = ((t.id % COLORES.length) + COLORES.length) % COLORES.length;
    colores[t.id] = hex === COLOR_TARIFA_POR_DEFECTO ? COLORES[enPaleta] : hex;
  });
  return colores;
};

// colorDeUbicacion: el de su tarifa (la primera, si vende varias); sin
// tarifas, gris. El diseño no tiene colores propios.
export const colorDeUbicacion = (sector, colores) => {
  const id = [...(sector.tarifas || [])].sort((a, b) => a - b).find((t) => colores[t]);
  return id ? colores[id] : COLOR_NEUTRO;
};

// sectorDeTarifa: la clave del sector donde se vende la tarifa en el mapa
export const sectorDeTarifa = (mapa, idtarifa) =>
  mapa.sectores.find((s) => !s.editor?.parteDe && s.tarifas.includes(idtarifa))?.clave ?? null;

// fijaPorVentas: una tarifa con ventas no cambia del sector donde está
// publicada; si no tenía sector, se puede ubicar (y reubicar) hasta publicar.
export const fijaPorVentas = (tarifa) => Boolean(tarifa.con_ventas && tarifa.idubicacion);

// motivoNoMover dice por qué una tarifa no puede pasar a ese sector, o null.
export const motivoNoMover = (mapa, tarifa, sector) => {
  const actual = sectorDeTarifa(mapa, tarifa.id);
  if (actual === sector.clave) return null;
  if (fijaPorVentas(tarifa) && sector.id !== tarifa.idubicacion) return "Tiene ventas: no se puede cambiar de ubicación.";
  if (tarifa.venta_mesa === "mesa" && sector.mesas.length === 0) return "Vende mesas enteras: va en una ubicación con mesas.";
  return null;
};

// quitarTarifa la saca de su ubicación. Una entrada siempre necesita una
// ubicación para publicar: queda pendiente hasta asignarla de nuevo.
export const quitarTarifa = (mapa, idtarifa) =>
  sincronizarPartes({
    ...mapa,
    sectores: mapa.sectores.map((s) => (s.tarifas.includes(idtarifa) ? { ...s, tarifas: s.tarifas.filter((t) => t !== idtarifa) } : s)),
  });

// moverTarifa la saca de donde esté y la pone en el sector: una tarifa se
// vende en un solo sector (en una parte, va a su ubicación).
export const moverTarifa = (mapa, idtarifa, claveSector) => {
  const destino = mapa.sectores.find((s) => s.clave === claveSector);
  const clave = destino?.editor?.parteDe || claveSector;
  return sincronizarPartes({
    ...mapa,
    sectores: mapa.sectores.map((s) => {
      const sin = s.tarifas.filter((t) => t !== idtarifa);
      if (s.clave === clave) return { ...s, tarifas: [...sin, idtarifa] };
      return sin.length === s.tarifas.length ? s : { ...s, tarifas: sin };
    }),
  });
};
